import { type MonitoringEvent, setMonitoringSink } from '@/utils/logger';

export const MONITORING_EVENT_NAME = 'southbay:monitoring';

declare global {
  interface Window {
    __SBY_MONITORING_SINK__?: (event: MonitoringEvent) => void;
    dataLayer?: Record<string, unknown>[];
  }
}

let isMonitoringConfigured = false;

const dispatchMonitoringEvent = (event: MonitoringEvent) => {
  if (typeof window === 'undefined') {
    return;
  }
  window.dispatchEvent(
    new CustomEvent(MONITORING_EVENT_NAME, {
      detail: event,
    }),
  );
};

const pushToDataLayer = (event: MonitoringEvent) => {
  if (typeof window === 'undefined' || !Array.isArray(window.dataLayer)) {
    return;
  }
  window.dataLayer.push({
    event: 'southbay_monitoring',
    ...event,
  });
};

const callWindowMonitoringSink = (event: MonitoringEvent) => {
  if (typeof window === 'undefined') {
    return;
  }
  window.__SBY_MONITORING_SINK__?.(event);
};

const sendToMonitoringEndpoint = (event: MonitoringEvent) => {
  if (typeof window === 'undefined') {
    return;
  }

  const endpoint = process.env.MONITORING_ENDPOINT;
  if (!endpoint) {
    return;
  }

  const payload = JSON.stringify(event);
  const blob = new Blob([payload], { type: 'application/json' });

  if (typeof navigator.sendBeacon === 'function') {
    navigator.sendBeacon(endpoint, blob);
    return;
  }

  void fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: payload,
    keepalive: true,
  }).catch(() => undefined);
};

export const configureMonitoringSink = () => {
  if (isMonitoringConfigured) {
    return;
  }

  setMonitoringSink((event) => {
    dispatchMonitoringEvent(event);
    pushToDataLayer(event);
    callWindowMonitoringSink(event);
    sendToMonitoringEndpoint(event);
  });

  isMonitoringConfigured = true;
};

export const resetMonitoringSinkForTests = () => {
  setMonitoringSink(undefined);
  isMonitoringConfigured = false;
};
